import React, { ComponentType, JSX, LazyExoticComponent, lazy } from 'react'
import {
  ActionFunction,
  ActionFunctionArgs,
  LoaderFunction,
  LoaderFunctionArgs,
  type RouteObject,
  type ShouldRevalidateFunction,
  type ShouldRevalidateFunctionArgs,
  useSearchParams,
} from 'react-router'

import { parseInitData } from '../app/shared/utils'
import { CachedLoaderDataContextProvider } from './hooks/useCachedLoaderData'
import { RouteViewParamProvider } from './hooks/useRouteViewParam'

/**
 * This fn creates a revalidator rule which always revalidates loader when the
 * specified keys change but ignores changes to other keys. It also allows
 * `revalidator.revalidate` to work even if the query didn’t
 */

export function revalidateOnQueryChange(keys: string[]): ShouldRevalidateFunction {
  return (args) => {
    const prevParams = args.currentUrl.searchParams
    const nextParams = args.nextUrl.searchParams
    const requiredChanged = keys.some((key) => prevParams.get(key) !== nextParams.get(key))
    const otherChanged = [...prevParams.keys(), ...nextParams.keys()].some(
      (key, index, arr) =>
        // Get unique keys
        !keys.includes(key) &&
        arr.indexOf(key) === index &&
        // Check changed keys
        prevParams.get(key) !== nextParams.get(key)
    )

    if (!requiredChanged && otherChanged) {
      return false
    }

    return args.defaultShouldRevalidate
  }
}

/**
 * This fn does the same as `revalidateOnQueryChange`
 * except you need to provide `ShouldRevalidateFunctionArgs` manually
 */

export function shouldRevalidateOnQueryChange(args: ShouldRevalidateFunctionArgs, keys: string[]) {
  return revalidateOnQueryChange(keys)(args)
}

export function parseRouteInitData<DataType>({
  elementId = 'route-init-data',
  fallback = null,
  removeElement = true,
}: { elementId?: string; fallback?: DataType | null; removeElement?: boolean } = {}): DataType | null {
  const element = document.getElementById(elementId)
  let initialData = fallback

  if (!element) return fallback

  try {
    const parsedData = parseInitData<DataType>(elementId)
    if (removeElement) element.remove()

    if (parsedData) initialData = parsedData
  } catch {
    initialData = fallback
  }

  return initialData
}

/**
 * Function which wraps React.lazy and checks whether a chunk is preloaded
 * from async-manifest. If yes, kick in the promise resolve early so we can
 * instantly render the component
 */
export function lazyLoadComponent<Props>({
  chunkName,
  load,
}: {
  chunkName: string
  load: () => Promise<{ default: ComponentType<Props> }>
}): LazyExoticComponent<ComponentType<Props>> {
  // Resolve earlier if prefetch element is present
  const hasPreloadElement = document.querySelector(`[data-chunk-id=${chunkName}]`)

  if (hasPreloadElement) {
    const componentLoader = load()

    return lazy(() => componentLoader)
  }

  return lazy(load)
}

export type LazyRouteDefinition = Omit<RouteObject, 'Component'> & {
  isNullValidData?: boolean
  loaderBundle?: () => Promise<{ loader?: LoaderFunction; action?: ActionFunction }>
  Component?: () => JSX.Element
  LazyComponent?: () => Promise<{ default: React.ComponentType }>
}

/**
 * Transforms top level async routes and their loaders to react router route definitions.
 * - `loaderBundle` must return promise module with exports `{ loader?, action? }`
 * - `Component` can be a react component or `lazyLoadComponent`.
 *
 * If `loaderBundle` and `Component` is set, the component is automatically wrapped in
 * `<CachedLoaderDataContextProvider />`
 *
 * Do not use this for query param routes
 */
export function defineLazyRoutes(routes: LazyRouteDefinition[]): RouteObject[] {
  return routes.map(
    ({ isNullValidData, loaderBundle, LazyComponent, Component: ComponentSync, ...props }) =>
      ({
        ...props,
        lazy:
          props.lazy ??
          (async () => {
            const [lazyComponentModule, loaderBundleModule] = await Promise.all([LazyComponent?.(), loaderBundle?.()])
            const Component = ComponentSync ?? lazyComponentModule?.default
            const action = loaderBundleModule?.action
            const loader = loaderBundleModule?.loader
            return {
              action,
              loader,
              // We’re rewriting Component if we have a loader
              Component: loader && Component ? undefined : Component,
              // Wrap each route in its own provider so they don’t share loader cache
              element:
                loader && Component ? (
                  <CachedLoaderDataContextProvider isNullValidData={isNullValidData}>
                    <Component />
                  </CachedLoaderDataContextProvider>
                ) : undefined,
            }
          }),
        children: defineLazyRoutes((props.children as any) ?? []),
      }) as RouteObject
  )
}

/**
 * Argument to `createModuleViewSwitch` when manually specifying the route
 */
export type ObjectRoute = {
  useDefaultLoader?: boolean
  Component?: ReturnType<typeof lazyLoadComponent>
  LazyComponent?: () => Promise<{ default: React.ComponentType }>
  loader?: () => Promise<{ loader: LoaderFunction }>
  action?: () => Promise<{ action: ActionFunction }>
}

/**
 * A very thin and naive abstraction over React router which allows routing
 * by (single) query params
 *
 * @param options.viewParam - The name of the view parameter
 * @param options.viewParamParser - Optional parser for the view parameter. If not provided, the view parameter is treated as a string.
 * @param options.defaultView - The default view to show if the view selected by viewParam is not present
 * @param options.views - An object mapping view names to route definitions
 * @param options.skipNotFound - Whether to skip the route if it is not found instead of using the default view
 */
export function createRouteViewSwitch<ViewParamType extends string, ViewType extends string>(options: {
  viewParam: ViewParamType
  viewParamParser?: (viewParam: string | null) => ViewType | null | undefined
  defaultView: ViewType | null
  views: Record<string, ObjectRoute>
  skipNotFound?: boolean
}) {
  /**
   * Load the module (should probably be replaced with our async utils code or
   * react query so we get retry). Needs to stay `async`, so we can await in
   * action/loader switch even if the module is of `ObjectModule` type
   */
  function getModuleFromURL(search: URLSearchParams): {
    param: ViewType | null | undefined
    route: ObjectRoute | undefined
    skipped: boolean
  } {
    const parser = options.viewParamParser ?? ((viewParam) => viewParam as ViewType)
    const viewQueryParam = parser(search.get(options.viewParam))
    const route = viewQueryParam && options.views[viewQueryParam]
    const skipped = !route && options.skipNotFound

    return {
      param: viewQueryParam ?? (skipped ? undefined : options.defaultView),
      route: route ?? (skipped ? undefined : options.views[options.defaultView!]),
      skipped: !!skipped,
    }
  }

  function action(args: ActionFunctionArgs<any>): ReturnType<ActionFunction> {
    const url = new URL(args.request.url)
    const { route } = getModuleFromURL(url.searchParams)
    if (!route?.action) return null
    return route.action().then((actionModule) => actionModule.action(args))
  }

  function loader(args: LoaderFunctionArgs<any>): ReturnType<LoaderFunction> {
    const url = new URL(args.request.url)
    const { route } = getModuleFromURL(url.searchParams)

    // Use default loader if none is defined
    if (!route?.loader) return parseRouteInitData()

    return route.loader().then((loaderModule) => loaderModule.loader(args))
  }

  // Handle the actual view switching
  function ViewSwitch() {
    const [searchParams] = useSearchParams()
    const { route, param, skipped } = getModuleFromURL(searchParams)
    if (skipped) return { Component: undefined }

    const Component = route?.Component

    return (
      <RouteViewParamProvider viewParam={param}>
        <CachedLoaderDataContextProvider children={Component && <Component />} />
      </RouteViewParamProvider>
    )
  }

  async function lazySwitch() {
    const { route, param, skipped } = getModuleFromURL(new URLSearchParams(window.location.search))
    if (skipped) return { Component: undefined }

    const modulePromise = await route?.LazyComponent?.()
    const Component = modulePromise?.default

    return {
      Component: () => (
        <RouteViewParamProvider viewParam={param}>
          <CachedLoaderDataContextProvider children={Component && <Component />} />
        </RouteViewParamProvider>
      ),
    }
  }

  return {
    defaultView: options.defaultView,
    action,
    loader,
    ViewSwitch,
    lazySwitch,
  }
}
